import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ScrollText } from "lucide-react";
import BlocksRenderer from "./blocks/Blocks";

/** Must match Layout header (h-16) and SlideDeckNav (h-14). */
export const SITE_HEADER_H = "4rem";
export const SLIDE_NAV_H = "3.5rem";
export const SLIDE_STACK_TOP = `calc(${SITE_HEADER_H} + ${SLIDE_NAV_H})`;

// One slide per H2 section (PRD §6.4). Leading H1 / preamble before the first H2 is skipped
// — the page title is already shown in the PageView hero.
const buildSlides = (blocks) => {
  const slides = [];
  let current = null;

  blocks.forEach((b) => {
    const isH2 = b.type === "heading" && b.level === 2;
    if (isH2) {
      current = [b];
      slides.push(current);
    } else if (current !== null) {
      current.push(b);
    }
  });

  if (!slides.length && blocks.length) {
    const start =
      blocks[0]?.type === "heading" && blocks[0]?.level === 1 ? 1 : 0;
    const rest = blocks.slice(start);
    if (rest.length) slides.push(rest);
  }

  return slides;
};

function SlideDeckNav({ idx, total, onPrev, onNext, onExit }) {
  return (
    <nav
      className="fixed left-0 right-0 z-[55] h-14 border-b border-pillet-sage/40 bg-pillet-forest shadow-[0_4px_24px_rgba(0,0,0,0.45)]"
      style={{ top: SITE_HEADER_H }}
      data-testid="slide-deck-nav"
      aria-label="Navigation du diaporama"
    >
      <div className="max-w-6xl mx-auto h-full px-4 sm:px-5 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onPrev}
          disabled={idx === 0}
          data-testid="slide-prev"
          className="flex items-center gap-1.5 pillet-dashed px-3 sm:px-4 py-2 font-heading uppercase text-xs tracking-widest text-pillet-sage hover:bg-pillet-sage hover:text-black transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-pillet-sage shrink-0"
        >
          <ChevronLeft size={16} />
          <span className="hidden sm:inline">Précédent</span>
        </button>

        <span
          className="font-heading text-sm text-white tracking-widest text-center shrink-0"
          data-testid="slide-indicator"
        >
          {idx + 1}
          <span className="text-pillet-muted"> / {total}</span>
        </span>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onNext}
            disabled={idx === total - 1}
            data-testid="slide-next"
            className="flex items-center gap-1.5 pillet-dashed px-3 sm:px-4 py-2 font-heading uppercase text-xs tracking-widest text-pillet-sage hover:bg-pillet-sage hover:text-black transition-colors disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-pillet-sage"
          >
            <span className="hidden sm:inline">Suivant</span>
            <ChevronRight size={16} />
          </button>
          {onExit && (
            <button
              type="button"
              onClick={onExit}
              data-testid="slide-exit"
              className="hidden sm:flex items-center gap-1.5 pillet-dashed px-3 py-2 font-heading uppercase text-xs tracking-widest text-white/80 hover:bg-pillet-sage hover:text-black transition-colors"
              title="Quitter le mode présentation"
            >
              <ScrollText size={15} />
              Lecture
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}

const SlideDeck = ({ blocks, onExit }) => {
  const slides = buildSlides(blocks);
  const [idx, setIdx] = useState(0);
  const total = slides.length;

  const go = useCallback(
    (d) => setIdx((i) => Math.min(total - 1, Math.max(0, i + d))),
    [total],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape" && onExit) onExit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onExit]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.dataset.slideDeck = "true";
    return () => {
      document.body.style.overflow = prev;
      delete document.documentElement.dataset.slideDeck;
    };
  }, []);

  if (!total) return null;

  return (
    <>
      <SlideDeckNav
        idx={idx}
        total={total}
        onPrev={() => go(-1)}
        onNext={() => go(1)}
        onExit={onExit}
      />
      <div
        className="fixed inset-x-0 bottom-0 z-[50] bg-pillet-bg overflow-y-auto overscroll-contain"
        style={{ top: SLIDE_STACK_TOP }}
        data-testid="slide-deck"
      >
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-12 min-h-full flex flex-col justify-center">
          <div key={idx} className="pillet-reveal">
            <BlocksRenderer nodes={slides[idx] || []} />
          </div>
        </div>
      </div>
    </>
  );
};

export default SlideDeck;
